import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["options", "endDateField", "preview", "previewBtn", "checkInToggle"]

  connect() {
    // Only initialize if the recurrence UI is present (feature flag enabled)
    if (this.hasOptionsTarget) {
      this.toggleRecurrence()
    }
  }

  toggleRecurrence(event) {
    // Only proceed if the recurrence UI is present (feature flag enabled)
    if (!this.hasOptionsTarget) return

    const checkbox = event?.target || this.element.querySelector('[name="reservation[repeat_freq]"]')
    const isRecurring = checkbox.checked

    this.optionsTarget.classList.toggle('is-open', isRecurring)
    if (!isRecurring && this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }

    // Checking in applies to a single occurrence, not a series being created.
    if (this.hasCheckInToggleTarget) {
      this.checkInToggleTarget.hidden = isRecurring
      if (isRecurring) {
        const checkInCheckbox = this.checkInToggleTarget.querySelector('input[type="checkbox"]')
        if (checkInCheckbox) checkInCheckbox.checked = false
      }
    }
  }

  toggleForever(event) {
    const isForever = event.target.checked
    this.endDateFieldTarget.disabled = isForever
  }

  async preview(event) {
    event.preventDefault()

    const form = this.element.closest('form')
    const formData = new FormData(form)

    // A Rails check_box renders a hidden companion *before* the input, so these fields
    // appear twice in the form data and FormData#get returns the first - the hidden one.
    // For `forever` that is the string "0", which is truthy, so the preview always asked
    // for a forever series and ignored the Until date entirely; for `repeat_freq` it
    // always sent "Never", which only looked right because the creator falls back to a
    // weekly interval for values it doesn't recognise. Rails reads the last value, so
    // do the same.
    const lastValue = (name) => {
      const values = formData.getAll(name)
      return values.length ? values[values.length - 1] : ''
    }

    const params = new URLSearchParams({
      'reservation[member_id]': lastValue('reservation[member_id]'),
      'reservation[repeat_freq]': lastValue('reservation[repeat_freq]') || 'Never',
      'reservation[interval_weeks]': lastValue('reservation[interval_weeks]') || '1',
      'reservation[last_reservation_date]': lastValue('reservation[last_reservation_date]'),
      'reservation[forever]': lastValue('reservation[forever]') === '1' ? '1' : '0'
    })

    try {
      const response = await fetch(`${this.getNamespacePath()}/occurrences/${this.getOccurrenceId()}/reservations/preview_recurring?${params}`, {
        headers: { 'Accept': 'application/json' }
      })

      const data = await response.json()

      if (data.errors && data.errors.length > 0) {
        this.previewTarget.innerHTML = `
          <div class="alert alert-danger mb-0">
            <strong>Cannot create series:</strong>
            <ul class="mb-0">
              ${data.errors.map(e => `<li>${this.escapeHtml(e)}</li>`).join('')}
            </ul>
          </div>
        `
      } else if (data.occurrences && data.occurrences.length > 0) {
        const datesList = data.occurrences.slice(0, 10)
          .map(o => `<li>${this.escapeHtml(o.date)} - ${this.escapeHtml(o.title)}</li>`)
          .join('')
        const remaining = data.occurrences.length > 10 ? `<li>...and ${data.occurrences.length - 10} more</li>` : ''

        this.previewTarget.innerHTML = `
          <div class="alert alert-info mb-0">
            <strong>Will create ${data.occurrences.length} reservations:</strong>
            <ul class="mb-0">
              ${datesList}
              ${remaining}
            </ul>
          </div>
          ${this.skippedHtml(data.skipped)}
        `
      } else {
        this.previewTarget.innerHTML = `
          <div class="alert alert-warning mb-0">No matching occurrences found</div>
          ${this.skippedHtml(data.skipped)}
        `
      }
    } catch (error) {
      this.previewTarget.innerHTML = `<div class="alert alert-danger mb-0">Error loading preview: ${this.escapeHtml(error.message)}</div>`
      console.error('Preview error:', error)
    }
  }

  // Dates in the pattern with no bookable class. The server has always sent these; until
  // now nothing rendered them, so the preview promised "Will create N" while saying nothing
  // about the gaps. The flash after creation reports a count and relies on this for detail.
  skippedHtml(skipped) {
    if (!skipped || skipped.length === 0) return ''

    const shown = skipped.slice(0, 10)
      .map(d => `<li>${this.escapeHtml(d)}</li>`)
      .join('')
    const remaining = skipped.length > 10 ? `<li>...and ${skipped.length - 10} more</li>` : ''

    return `
      <div class="alert alert-warning mb-0 mt-2">
        <strong>Skipping ${skipped.length} date${skipped.length === 1 ? '' : 's'} with no class available:</strong>
        <ul class="mb-0">
          ${shown}
          ${remaining}
        </ul>
      </div>
    `
  }

  getOccurrenceId() {
    return document.querySelector('[name="occurrence_id"]')?.value || ''
  }

  getNamespacePath() {
    const namespace = document.querySelector('[name="namespace"]')?.value || 'branch'
    return `/${namespace}`
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
